"use client";

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import { isDefined } from "@/shared/lib/utils/isDefined";

import { loginWithPassword, logoutFromBff } from "./auth-api";
import {
  clearAuthSession,
  getAuthSessionSnapshot,
  getServerAuthSnapshot,
  persistAuthSession,
  setAuthSessionCookiePresent,
  subscribeAuthSession,
  type StoredAuthUser,
} from "./auth-session";

export type AuthUser = StoredAuthUser;

interface AuthContextValue {
  authUser: AuthUser | undefined;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | undefined;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const authUser = useSyncExternalStore(
    subscribeAuthSession,
    () => getAuthSessionSnapshot().user,
    () => getServerAuthSnapshot().user,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useLayoutEffect(() => {
    if (authUser) setAuthSessionCookiePresent();
  }, [authUser]);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await loginWithPassword(email, password);
      persistAuthSession(result.accessToken, result.user);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed.");
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutFromBff();
    } finally {
      clearAuthSession();
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      authUser,
      isAuthenticated: isDefined(authUser),
      loading,
      error,
      login,
      logout,
    }),
    [authUser, loading, error, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return context;
}
